/**
 * Global auth interceptor - signs user out on invalid/expired JWT (401/403).
 * Import this file once in App.js as a side-effect.
 *
 * Only applies to courses-api / HQ API calls. S3 presigned PUT failures also
 * return 403 and must not clear the candidate session.
 */
import axios from "axios";
import constants from "./Constants.js";
import {
  clearCandidateSession,
  isCandidateLoggedIn,
} from "./utils/candidateAuth.js";

let isLoggingOut = false;

const protectedApiOrigins = [constants.baseURL, constants.hqApiURL]
  .filter(Boolean)
  .map((base) => {
    try {
      return new URL(base).origin;
    } catch {
      return null;
    }
  })
  .filter(Boolean);

function requestOrigin(url) {
  if (!url) return null;
  try {
    return new URL(url, window.location.origin).origin;
  } catch {
    return null;
  }
}

function isProtectedApiRequest(url) {
  const origin = requestOrigin(url);
  return origin != null && protectedApiOrigins.includes(origin);
}

function axiosRequestUrl(config) {
  if (!config) return "";
  const url = config.url || "";
  if (/^https?:\/\//i.test(url)) return url;
  const base = (config.baseURL || "").replace(/\/$/, "");
  const path = url.replace(/^\//, "");
  return base ? `${base}/${path}` : path;
}

function fetchRequestUrl(input) {
  if (typeof input === "string") return input;
  if (input instanceof Request) return input.url;
  if (input?.url) return input.url;
  return "";
}

function handleAuthFailure() {
  if (!isCandidateLoggedIn()) return;
  if (isLoggingOut) return;
  isLoggingOut = true;

  clearCandidateSession();

  const next = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.href = next === "%2F" ? "/login" : `/login?next=${next}`;
}

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    if ((status === 401 || status === 403) && isProtectedApiRequest(axiosRequestUrl(error?.config))) {
      handleAuthFailure();
    }
    return Promise.reject(error);
  }
);

const originalFetch = window.fetch;
window.fetch = async (...args) => {
  const response = await originalFetch(...args);
  if (
    (response.status === 401 || response.status === 403) &&
    isProtectedApiRequest(fetchRequestUrl(args[0]))
  ) {
    handleAuthFailure();
  }
  return response;
};
